import { type ReactNode, createContext, useContext, useRef } from "react";
import { createStore, useStore } from "zustand";

import { Values } from "@/constants";
import { State } from "@/state";

export type GroupType = string;

export type ColumnDefinitionType = {
  objectKey: string;
  label: string;
  width?: number;
  widthMode?: "pixel" | "percentage";
  originalIndex?: number;
};

export type TableStoreType = {
  activeRowId: string;
  checkedRows: { [rowId: string]: boolean };
  columnDefinitions: ColumnDefinitionType[];
  editingCellFocusTrigger: number;
  getColumnWidth: (colIdx: number) => number;
  getRowDataFromRowIndex: (rowIndex: number) => any;
  getRowIdFromRowData: (rowData: any) => string;
  isAllRowsChecked: boolean;
  layoutIndexToColumnIndex: number[];
  onRowClick: (rowData: any, isDisabled: boolean, isAddNew: boolean) => void;
  setActiveRowId: (rowId: string, typename?: string) => void;
  setColumnDefinitions: (columns: ColumnDefinitionType[]) => void;
  setColumnWidth: (colIdx: number, width: number) => void;
  setIsAllRowsChecked: (allRowsChecked: boolean) => void;
  setCheckedRows: (checkedRows: { [rowId: string]: boolean }) => void;
  setTotalCount: (totalCount: number) => void;
  shiftLayoutIndex: (layoutIndex: number, shiftBy: number) => void;
  toggleIsRowChecked: (rowId: string) => void;
  totalCount: number;
  unlockedColumnsStartIndex: number;
};

function createTableStoreState(
  set: (
    partial:
      | Partial<TableStoreType>
      | ((state: TableStoreType) => Partial<TableStoreType>),
  ) => void,
  get: () => TableStoreType,
): TableStoreType {
  return {
    activeRowId: null,
    isAllRowsChecked: false,
    checkedRows: {},
    columnDefinitions: [],
    editingCellFocusTrigger: 0,
    getColumnWidth: (colIdx) => {
      const columnDefs = get().columnDefinitions;
      if (colIdx < 0 || colIdx >= columnDefs.length) return 200;
      return columnDefs?.[colIdx]?.width || Values.DEFAULT_COLUMN_WIDTH;
    },
    getRowDataFromRowIndex: () => null,
    getRowIdFromRowData: (row) => row?.id ?? row?.identifier ?? "",
    layoutIndexToColumnIndex: [],
    onRowClick: () => null,
    setActiveRowId: (rowId, typename) => {
      set({ activeRowId: rowId });
      if (typename) {
        State.getState().setActivePanelNodeId(rowId, { autofocus: false });
      }
    },
    setColumnDefinitions: (columns) => {
      set({ columnDefinitions: columns });
    },
    setColumnWidth: (colIdx, width) => {
      const columnDefinitions = [...get().columnDefinitions];
      if (colIdx >= 0 && colIdx < columnDefinitions.length) {
        columnDefinitions[colIdx] = {
          ...columnDefinitions[colIdx],
          width,
        };
        set({ columnDefinitions });
      }
    },
    setIsAllRowsChecked: (allRowsChecked) => {
      set({ isAllRowsChecked: allRowsChecked });
    },
    setCheckedRows(checkedRows) {
      set({ checkedRows });
    },
    setTotalCount: (numRows) => set({ totalCount: numRows }),
    shiftLayoutIndex: (layoutIndex, shiftBy) => {
      const layoutIndexToColumnIndex = [...get().layoutIndexToColumnIndex];
      const lastIndex = layoutIndexToColumnIndex.length - 1;

      if (
        (layoutIndex === 0 && shiftBy < 0) ||
        (layoutIndex === lastIndex && shiftBy > 0)
      ) {
        if (shiftBy > 0) {
          const last = layoutIndexToColumnIndex[lastIndex];
          for (let i = lastIndex; i > 0; i--) {
            layoutIndexToColumnIndex[i] = layoutIndexToColumnIndex[i - 1];
          }
          layoutIndexToColumnIndex[0] = last;
        } else {
          const first = layoutIndexToColumnIndex[0];
          for (let i = 0; i < lastIndex; i++) {
            layoutIndexToColumnIndex[i] = layoutIndexToColumnIndex[i + 1];
          }
          layoutIndexToColumnIndex[lastIndex] = first;
        }
      } else {
        let swapIndex = layoutIndex + shiftBy;
        if (swapIndex < 0) swapIndex = lastIndex;
        if (swapIndex > lastIndex) swapIndex = 0;

        const temp = layoutIndexToColumnIndex[layoutIndex];
        layoutIndexToColumnIndex[layoutIndex] =
          layoutIndexToColumnIndex[swapIndex];
        layoutIndexToColumnIndex[swapIndex] = temp;
      }

      set({ layoutIndexToColumnIndex });
    },
    toggleIsRowChecked: (id) => {
      const checkedRows = { ...get().checkedRows };
      checkedRows[id] = !checkedRows[id];
      let allRowsChecked = get().isAllRowsChecked;
      if (!checkedRows[id]) {
        allRowsChecked = false;
      }
      set({ checkedRows, isAllRowsChecked: allRowsChecked });
    },
    totalCount: 0,
    unlockedColumnsStartIndex: 1,
  };
}

export type TableStoreApi = ReturnType<typeof createTableStore>;

export const TableStoreContext = createContext<TableStoreApi | null>(null);

export function TableStoreProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<TableStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createTableStore();
  }
  return (
    <TableStoreContext.Provider value={storeRef.current}>
      {children}
    </TableStoreContext.Provider>
  );
}

export function createTableStore() {
  return createStore<TableStoreType>()((set, get) =>
    createTableStoreState(set, get),
  );
}

export function useTableStoreApi(): TableStoreApi {
  const store = useContext(TableStoreContext);
  if (!store) {
    throw new Error("useTableStore must be used within a <Table> component");
  }
  return store;
}

export function useTableStore<T>(selector: (state: TableStoreType) => T): T {
  const store = useTableStoreApi();
  return useStore(store, selector);
}
